import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Shield, FileCheck, Users, Award, Clock, TrendingUp } from 'lucide-react';
import SEO from '@/components/SEO';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PricingTable from '@/components/PricingTable';
import TestimonialSlider from '@/components/TestimonialSlider';
import ProcessSlider from '@/components/ProcessSlider';
import TestimonialFAQSection from '@/components/TestimonialFAQSection';
import ContactPopup from '@/components/ContactPopup';
import MobileSlider from '@/components/MobileSlider';
import { Button } from '@/components/ui/button';
import { openChat } from '@/utils/chatService';
import { fetchGeolocation, submitLead, handleLeadResponse } from '@/services/leadService';

const Home = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const navigate = useNavigate();

  // Fetch geolocation on mount
  useEffect(() => {
    const loadGeoData = async () => {
      const data = await fetchGeolocation();
      setGeoData(data);
    };
    loadGeoData();
  }, []);

  const features = [
    {
      icon: Shield,
      title: 'Complete Protection',
      description: 'Comprehensive trademark, copyright, and patent protection services for your intellectual property.'
    },
    {
      icon: FileCheck,
      title: 'Expert Filing',
      description: 'Meticulous application preparation and filing with maximum approval rates.'
    },
    {
      icon: Users,
      title: 'Dedicated Support',
      description: 'Personalized case managers guide you through every step of the process.'
    },
    {
      icon: Award,
      title: 'Proven Success',
      description: 'Thousands of successfully registered trademarks and satisfied clients nationwide.'
    },
    {
      icon: Clock,
      title: 'Fast Turnaround',
      description: 'Efficient processing with regular updates on your application status.'
    },
    {
      icon: TrendingUp,
      title: 'Growth Partners',
      description: 'Strategic advice to help your brand grow and expand with confidence.'
    }
  ];

  const services = [
    {
      title: 'Trademark Services',
      description: 'Protect your brand identity with comprehensive trademark registration and monitoring.',
      icon: Shield,
      link: '/services/trademark-registration'
    },
    {
      title: 'Company Formation',
      description: 'LLC, C Corp, S Corp, and Non-Profit formation services tailored to your business needs.',
      icon: FileCheck,
      link: '/services/business-formation'
    },
    {
      title: 'Copyright Services',
      description: 'Secure your creative works with expert copyright registration and enforcement.',
      icon: Award,
      link: '/services/register-copyright'
    },
    {
      title: 'Patent Services',
      description: 'Protect your innovations with comprehensive patent search and application services.',
      icon: TrendingUp,
      link: '/services/patent-application'
    },
    {
      title: 'Legal Services',
      description: 'Expert legal support for business contracts, compliance, and intellectual property matters.',
      icon: Users,
      link: '/services/it-commercial-law'
    },
    {
      title: 'Tax & Advisory',
      description: 'Strategic tax planning and financial advisory services to optimize your business performance.',
      icon: Clock,
      link: '/services/tax-advisory'
    }
  ];

  return (
    <>
      <SEO 
        title="Easy Trademark Filing - Register & Protect Your Brand"
        description="Easy Trademark Filing makes trademark registration simple and affordable. Expert attorneys, 98% success rate, and 5,000+ brands protected. Start your free consultation today."
        keywords="easy trademark filing, trademark registration, brand protection, copyright services, patent filing, LLC formation, business formation, intellectual property"
      />
      <Header />
      
      {/* Hero Section - Enhanced Professional Design */}
      <section className="relative py-12 md:py-16 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="hero-section">
        {/* Enhanced Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(239,68,68,0.1),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(249,115,22,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        
        {/* Dynamic Decorative Elements */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-red-600/20 to-orange-500/20 rounded-full blur-3xl animate-pulse hidden md:block"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-orange-600/15 to-red-500/15 rounded-full blur-3xl hidden md:block"></div>
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 hidden md:block"></div>

        <div className="container mx-auto px-4 relative z-10 overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Left Side - Content */}
            <div className="space-y-6 text-white">
              <div className="inline-block">
                <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide">
                  Trusted by 5,000+ Businesses
                </span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="block">File Your Trademark</span>
                <span className="block mt-2 bg-gradient-to-r from-red-500 to-orange-400 bg-clip-text text-transparent">
                  The Easy Way
                </span>
              </h1>
              
              <p className="text-lg text-gray-300 leading-relaxed max-w-xl">
                Easy Trademark Filing takes the complexity out of trademark registration. Our expert attorneys handle everything so you can focus on growing your business.
              </p>

              {/* Stats Row - Compact */}
              <div className="grid grid-cols-3 gap-4 pt-4">
                <div>
                  <div className="text-2xl font-bold text-white mb-0.5">1000+</div>
                  <div className="text-xs text-gray-400">Trademarks</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white mb-0.5">98%</div>
                  <div className="text-xs text-gray-400">Success Rate</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white mb-0.5">24/7</div>
                  <div className="text-xs text-gray-400">Support</div>
                </div>
              </div>

              {/* Trust Badges - Compact */}
              <div className="flex flex-wrap gap-2 pt-2">
                <div className="flex items-center space-x-1.5 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/20">
                  <Shield className="w-4 h-4 text-red-500" />
                  <span className="text-xs">USPTO Registered</span>
                </div>
                <div className="flex items-center space-x-1.5 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/20">
                  <Award className="w-4 h-4 text-orange-500" />
                  <span className="text-xs">A+ Rated</span>
                </div>
                <div className="flex items-center space-x-1.5 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/20">
                  <Users className="w-4 h-4 text-red-500" />
                  <span className="text-xs">5000+ Clients</span>
                </div>
              </div>
            </div>

            {/* Right Side - Compact Contact Form */}
            <div className="bg-white rounded-2xl p-5 md:p-6 shadow-2xl border border-gray-100 w-full max-w-full overflow-hidden" data-testid="hero-form">
              <div className="mb-5">
                <div className="inline-block mb-2">
                  <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide">
                    🔥 Act Now
                  </span>
                </div>
                <h2 className="text-2xl font-bold mb-1 text-gray-900 leading-tight">
                  Protect Your Brand Before It's Too Late!
                </h2>
                <p className="text-sm text-gray-600">
                  <strong className="text-red-600">Free consultation</strong> · Expert guidance · 24-hour response
                </p>
              </div>

              <form onSubmit={async (e) => {
                e.preventDefault();
                setIsSubmitting(true);
                setSubmitError('');

                const formDataObj = new FormData(e.target);
                const formData = {
                  name: formDataObj.get('name'),
                  email: formDataObj.get('email'),
                  phone: formDataObj.get('phone'),
                  country_code: formDataObj.get('country_code'),
                  message: formDataObj.get('message')
                };

                // Validate phone number
                const phoneDigits = formData.phone.replace(/\D/g, '');
                if (phoneDigits.length !== 10) {
                  setSubmitError('Phone number must be exactly 10 digits');
                  setIsSubmitting(false);
                  return;
                }

                try {
                  const response = await submitLead(formData, geoData, 'homepage_hero');
                  
                  handleLeadResponse(
                    response,
                    (customerId) => {
                      window.location.href = `/application/main?customerid=${customerId}`;
                    },
                    (errorMsg) => {
                      setSubmitError(errorMsg);
                      setIsSubmitting(false);
                    }
                  );
                } catch (error) {
                  setSubmitError('An error occurred. Please try again.');
                  setIsSubmitting(false);
                }
              }} className="space-y-3">
                <div>
                  <label htmlFor="hero-name" className="block text-xs font-semibold text-gray-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    id="hero-name"
                    name="name"
                    type="text"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="John Doe"
                    data-testid="hero-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="hero-email" className="block text-xs font-semibold text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    id="hero-email"
                    name="email"
                    type="email"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="john@example.com"
                    data-testid="hero-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="hero-phone" className="block text-xs font-semibold text-gray-700 mb-1">
                    Phone Number *
                  </label>
                  <div className="flex gap-2 w-full min-w-0">
                    <select
                      name="country_code"
                      defaultValue="+1"
                      className="w-[100px] shrink-0 px-2 py-2.5 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white"
                      data-testid="hero-country-code"
                    >
                      <option value="+1">+1 (US/CA)</option>
                      <option value="+44">+44 (UK)</option>
                      <option value="+91">+91 (IN)</option>
                      <option value="+61">+61 (AU)</option>
                      <option value="+49">+49 (DE)</option>
                      <option value="+33">+33 (FR)</option>
                      <option value="+81">+81 (JP)</option>
                      <option value="+86">+86 (CN)</option>
                      <option value="+82">+82 (KR)</option>
                      <option value="+55">+55 (BR)</option>
                      <option value="+52">+52 (MX)</option>
                      <option value="+39">+39 (IT)</option>
                      <option value="+34">+34 (ES)</option>
                      <option value="+31">+31 (NL)</option>
                      <option value="+46">+46 (SE)</option>
                      <option value="+41">+41 (CH)</option>
                      <option value="+65">+65 (SG)</option>
                      <option value="+971">+971 (AE)</option>
                      <option value="+27">+27 (ZA)</option>
                      <option value="+64">+64 (NZ)</option>
                    </select>
                    <input
                      id="hero-phone"
                      name="phone"
                      type="tel"
                      required
                      className="flex-1 min-w-0 px-3 py-2.5 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="(555) 123-4567"
                      data-testid="hero-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="hero-message" className="block text-xs font-semibold text-gray-700 mb-1">
                    How can we help? *
                  </label>
                  <textarea
                    id="hero-message"
                    name="message"
                    required
                    rows={2}
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    placeholder="Tell us about your trademark needs..."
                    data-testid="hero-message-input"
                  />
                </div>

                {submitError && (
                  <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg">
                    {submitError}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-red-600 to-orange-500 text-white py-3 rounded-lg text-sm font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                  data-testid="hero-submit-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'Get Free Consultation'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>

              <p className="text-xs text-gray-500 text-center mt-3">
                🔒 Your information is secure and confidential
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section - Consistent Alignment */}
      <section className="py-20 bg-white" data-testid="informative-section">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 order-2 md:order-1">
              <h2 className="text-4xl md:text-5xl font-bold leading-tight">
                Why Trademark Registration Matters
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed">
                Your brand is your most valuable business asset. Without proper trademark protection, anyone can use 
                your name, logo, or slogan—causing confusion and lost revenue.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                At Easy Trademark Filing, we simplify the entire process. From comprehensive searches to successful USPTO 
                registration, our team of experienced attorneys handles every detail with precision and care.
              </p>
              <div className="flex gap-4 pt-4">
                <Button
                  onClick={() => setIsPopupOpen(true)}
                  className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <img
                src="https://images.unsplash.com/photo-1760947073215-13bf8f153dfa?w=1200&q=80"
                alt="Modern office building"
                className="rounded-3xl shadow-2xl w-full h-full object-cover"
                style={{ minHeight: '400px', maxHeight: '500px' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Consistent Alignment */}
      <section className="py-20 bg-gray-50" data-testid="why-choose-us-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose Easy Trademark Filing?</h2>
            <p className="text-xl text-gray-600">Simple process, expert guidance, and proven results for your brand protection</p>
          </div>

          <MobileSlider>
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div
                  key={idx}
                  className="bg-white p-8 rounded-2xl card-hover border border-gray-100 h-full"
                  data-testid={`feature-${idx}`}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-orange-500 rounded-2xl flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </MobileSlider>
        </div>
      </section>

      {/* Services Overview - 3 Column Grid Without Images */}
      <section className="py-20 bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 relative overflow-hidden" data-testid="services-section">
        {/* Abstract Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(239,68,68,0.05)_25%,rgba(239,68,68,0.05)_50%,transparent_50%,transparent_75%,rgba(249,115,22,0.05)_75%)] bg-[size:60px_60px]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(239,68,68,0.08),transparent_40%),radial-gradient(circle_at_80%_70%,rgba(249,115,22,0.08),transparent_40%)]"></div>
        </div>
        
        {/* Decorative Abstract Elements */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-gradient-to-br from-red-200/30 to-orange-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-gradient-to-tl from-orange-200/30 to-red-200/30 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-gradient-to-r from-red-100/20 to-orange-100/20 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-6 py-2 rounded-full text-sm font-semibold">
                What We Offer
              </span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">Our Services</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Comprehensive intellectual property and business solutions tailored to protect and grow your brand
            </p>
          </div>

          <MobileSlider>
            {services.map((service, idx) => {
              const Icon = service.icon;
              return (
                <div
                  key={idx}
                  className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-red-200 flex flex-col h-full"
                  data-testid={`service-${idx}`}
                >
                  {/* Icon */}
                  <div className="mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-orange-500 rounded-2xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">{service.title}</h3>
                  
                  {/* Description */}
                  <p className="text-gray-600 leading-relaxed mb-6 flex-grow">{service.description}</p>
                  
                  {/* CTA Button */}
                  <Button
                    onClick={() => navigate(service.link)}
                    className="w-full bg-gradient-to-r from-red-600 to-orange-500 text-white py-3 rounded-full font-semibold hover:shadow-lg transition-shadow mt-auto"
                    data-testid={`service-cta-${idx}`}
                  >
                    Learn More
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>

                  {/* Decorative Corner Element */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-red-600/5 to-transparent rounded-bl-full"></div>
                </div>
              );
            })}
          </MobileSlider>
        </div>
      </section>

      {/* Process Slider */}
      <ProcessSlider />

      {/* Additional Content Section - Image Left, Content Right */}
      <section className="py-20 bg-white" data-testid="additional-content-section">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Side - Image */}
            <div className="order-2 md:order-1">
              <img
                src="https://images.unsplash.com/photo-1729371568794-fb9c66ab09cf?w=1200&q=80"
                alt="Professional workspace"
                className="rounded-3xl shadow-2xl w-full h-full object-cover"
                style={{ minHeight: '400px', maxHeight: '500px' }}
              />
            </div>
            
            {/* Right Side - Content */}
            <div className="space-y-6 order-1 md:order-2">
              <h2 className="text-4xl md:text-5xl font-bold leading-tight text-gray-900">
                Built Around Your Success
              </h2>
              
              <p className="text-xl text-gray-700 leading-relaxed">
                Easy Trademark Filing was founded on a simple principle: trademark registration shouldn't be complicated 
                or expensive. We've streamlined the process so you get professional protection without the hassle.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Every client gets a dedicated case manager, transparent pricing, and regular updates on their application. 
                With a 98% approval rate and thousands of happy clients, we deliver results you can count on.
              </p>

              <div className="grid grid-cols-2 gap-6 pt-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                      <Shield className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Proven Track Record</h3>
                    <p className="text-sm text-gray-600">98% success rate in trademark approvals</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                      <Award className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Expert Team</h3>
                    <p className="text-sm text-gray-600">Experienced IP attorneys at your service</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Fast Processing</h3>
                    <p className="text-sm text-gray-600">Quick turnaround on all applications</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">24/7 Support</h3>
                    <p className="text-sm text-gray-600">Always here when you need us</p>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <Button
                  onClick={() => setIsPopupOpen(true)}
                  className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg"
                >
                  Get Started Today
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Table */}
      <PricingTable />

      {/* Testimonials & FAQs */}
      <TestimonialFAQSection />

      {/* CTA Section - Strong Call to Action with Background */}
      <section className="relative py-24 overflow-hidden" data-testid="cta-section">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1758599543278-32d9d073941e?w=1920&q=80" 
            alt="Professional business team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/90 to-slate-900/95"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-red-600/20 to-orange-600/20"></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-6 py-2 rounded-full text-sm font-semibold uppercase tracking-wide">
                Limited Time Offer
              </span>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Don't Wait—Protect Your Brand Before Someone Else Takes It
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-4 leading-relaxed">
              Every day without trademark protection puts your business at risk.
            </p>
            
            <p className="text-lg text-gray-300 mb-10 max-w-3xl mx-auto">
              Join 5,000+ businesses that chose Easy Trademark Filing for fast, affordable, and reliable trademark registration. 
              Get expert legal support and peace of mind knowing your brand is secure.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                onClick={() => setIsPopupOpen(true)}
                className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-10 py-6 rounded-full text-lg font-bold hover:shadow-2xl hover:scale-105 transition-all"
                data-testid="cta-get-started-btn"
              >
                Start Your Trademark Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={() => openChat()}
                variant="outline"
                className="border-2 border-white text-white px-10 py-6 rounded-full text-lg font-bold hover:bg-white hover:text-slate-900 transition-all"
                data-testid="cta-consultation-btn"
              >
                Get Free Consultation
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-white/90">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-400" />
                <span className="text-sm">98% Success Rate</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-400" />
                <span className="text-sm">Fast Processing</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-red-400" />
                <span className="text-sm">5,000+ Happy Clients</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default Home;